import { defineStore } from 'pinia'
import { getUserInfo as apiGetUserInfo, logout as apiLogout, login as apiLogin } from '@/api/auth'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { ElMessage } from 'element-plus'
// router不再使用，因为现在使用window.location.href进行导航

export const useUserStore = defineStore('user', {
  state: () => ({
    token: getToken(),
    userInfo: null,
    roles: [],
    permissions: [],
    groups: []
  }),

  getters: {
    userId: (state) => state.userInfo?.id,
    username: (state) => state.userInfo?.username,
    avatar: (state) => state.userInfo?.avatar,
    nickname: (state) => state.userInfo?.nickname,
    // 判断是否已登录
    isLoggedIn: (state) => !!state.token && !!state.userInfo
  },

  actions: {
    // 登录
    async login(userInfo) {
      try {
        const { username, password } = userInfo
        const response = await apiLogin({ username: username.trim(), password })
        const { token } = response.data
        this.token = token
        setToken(token)
        // 触发登录事件，通知其他标签页
        localStorage.setItem('auth_event', JSON.stringify({
          type: 'login',
          token
        }))
        // 登录后立即获取用户信息
        await this.getUserInfo()
        return response
      } catch (error) {
        console.error('登录失败:', error)
        throw error
      }
    },

    // 更新Token
    updateToken(token) {
      this.token = token
      setToken(token)
    },

    // 获取用户信息
    async getUserInfo() {
      try {
        const response = await apiGetUserInfo()
        const { user, roles, permissions, groups } = response.data
        
        // 更新store状态
        this.userInfo = user
        this.roles = (roles || []).map(r => typeof r === 'string' ? r : r.code)
        this.permissions = (permissions || []).map(p => typeof p === 'string' ? p : p.code)
        this.groups = (groups || []).map(g => typeof g === 'string' ? g : g.code)
        
        // 同步到localStorage
        localStorage.setItem('user-info', JSON.stringify(user))
        
        return response.data
      } catch (error) {
        console.error('获取用户信息失败:', error)
        throw error
      }
    },

    // 刷新用户信息
    async refreshUserInfo() {
      if (!this.token) {
        console.log('没有token，跳过刷新用户信息')
        return null
      }
      
      try {
        console.log('开始刷新用户信息')
        const userData = await this.getUserInfo()
        console.log('用户信息刷新成功:', this.userInfo?.nickname || this.userInfo?.username)
        return userData
      } catch (error) {
        console.error('刷新用户信息失败:', error)
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          console.warn('用户认证已失效，清除状态')
          this.clearUserState()
        }
        return null
      }
    },

    // 退出登录
    async logout() {
      try {
        await apiLogout()
        this.token = null
        this.userInfo = null
        removeToken()
        // 触发登出事件，通知其他标签页
        localStorage.setItem('auth_event', JSON.stringify({
          type: 'logout'
        }))
      } catch (error) {
        console.error('退出登录失败:', error)
        
        // 即使API调用失败，也要确保本地状态被清除
        console.log('API调用失败，确保本地状态清除')
        this.resetState()
        removeToken()
        
        // 强制刷新并跳转到登录页
        window.location.href = '/login?fromLogout=true'
      }
    },

    // 重置状态
    resetState() {
      this.token = ''
      this.userInfo = null
      this.roles = []
      this.permissions = []
      this.groups = []
      // 清除localStorage中的用户信息
      localStorage.removeItem('user-info')
    },

    // 清除用户状态（别名方法）
    clearUserState() {
      this.resetState()
      removeToken()
    },

    // 检查是否有指定权限
    hasPermission(permission) {
      if (!permission || !this.permissions) {
        return false
      }
      return this.permissions.includes(permission)
    },

    // 检查是否有指定角色
    hasRole(role) {
      if (!role || !this.roles) {
        return false
      }
      return this.roles.includes(role)
    },

    // 检查是否有指定用户组
    hasGroup(group) {
      if (!group || !this.groups) {
        return false
      }
      return this.groups.includes(group)
    },

    // 初始化多标签页同步
    initMultiTabSync() {
      window.addEventListener('storage', (event) => {
        if (event.key === 'auth_event') {
          try {
            const data = JSON.parse(event.newValue)
            if (data.type === 'login') {
              this.token = data.token
              setToken(data.token)
              this.getUserInfo()
              ElMessage.success('其他标签页已登录')
            } else if (data.type === 'logout') {
              this.token = null
              this.userInfo = null
              removeToken()
              ElMessage.warning('其他标签页已登出')
              window.location.href = '/login'
            }
          } catch (error) {
            console.error('处理多标签页同步事件失败:', error)
          }
        }
      })
    }
  }
})

// 导出一个初始化函数，用于确保store被正确创建
export function initUserStore(pinia) {
  return useUserStore(pinia)
}
